import { useState, type ReactNode } from 'react';
import { Box } from '@mui/material';
import logoOnLight from '../assets/decisionpoint-logo-light-bg.png';

export interface PublicLayoutProps {
  /** Card body — the screen-specific content (medallion, headline, message, action). */
  children: ReactNode;
  /**
   * Customer logo URL resolved from the link token. When absent (the customer
   * can't be resolved), the neutral DecisionPoint logo is shown as the fallback.
   */
  logoUrl?: string | null;
  /** Customer display name — the `alt` text for the customer logo. */
  customerName?: string | null;
  /** Optional muted footer rendered below the card (support line, attribution). */
  footer?: ReactNode;
  /** Card max width in px. */
  maxWidth?: number;
}

/** Polaris elevation for a floating public card (prototype `Approval Magic Link` Card). */
const CARD_SHADOW = '0 10px 40px rgba(4,29,51,0.10), 0 2px 6px rgba(4,29,51,0.05)';

/**
 * Bare, centered card on a plain background for **anonymous, token-authenticated**
 * pages (the magic-link approval landing, and any future public/token page).
 * Renders entirely OUTSIDE the `AppShell` — no sidebar, no TopBar, no MSAL, no
 * `whoami`. The header shows the **customer's** logo resolved from the link token,
 * falling back to the neutral DecisionPoint logo when the customer can't be
 * resolved. Everything is token-tied so it tracks the active color scheme.
 *
 * Distinct from {@link AuthLayout}: that frame is always the neutral DecisionPoint
 * pre-auth brand (sign-in / blocked / signed-out); this one carries per-customer
 * branding driven by the token, and phone-friendly for an email landing.
 */
export function PublicLayout({
  children,
  logoUrl,
  customerName,
  footer,
  maxWidth = 420,
}: PublicLayoutProps) {
  // A resolvable-but-unreachable customer logo (dead CDN link) degrades to the
  // neutral DecisionPoint mark rather than a broken-image icon on this
  // phone-facing public page.
  const [logoFailed, setLogoFailed] = useState(false);
  const showCustomerLogo = !!logoUrl && !logoFailed;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        bgcolor: 'background.default',
        color: 'text.primary',
        p: { xs: 2, sm: 3 },
      }}
    >
      <Box sx={{ width: '100%', maxWidth }}>
        <Box
          sx={{
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '12px',
            boxShadow: CARD_SHADOW,
            p: '38px 34px 30px',
            textAlign: 'center',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: '26px' }}>
            {showCustomerLogo ? (
              <Box
                component="img"
                src={logoUrl}
                alt={customerName ?? ''}
                onError={() => setLogoFailed(true)}
                sx={{ height: 34, maxWidth: 220, width: 'auto', objectFit: 'contain' }}
              />
            ) : (
              <Box
                component="img"
                src={logoOnLight}
                alt="DecisionPoint"
                sx={{ height: 26, width: 'auto' }}
              />
            )}
          </Box>
          {children}
        </Box>
        {footer && (
          <Box
            sx={{
              mt: '20px',
              textAlign: 'center',
              color: 'text.secondary',
              fontSize: 12,
              lineHeight: 1.5,
            }}
          >
            {footer}
          </Box>
        )}
      </Box>
    </Box>
  );
}
